import { cn } from "../../../lib/utils";
import { Block as BlockType, Suggestion } from "../../../types/document";
import {
  resolveSentimentBackgroundColor,
  resolveSentimentBorderColor,
  resolveSentimentSecondaryBorderColor,
} from "./blockUtils";

interface BlockContainerProps {
  block: BlockType;
  suggestion?: Suggestion;
  isSelected: boolean;
  isExpanded: boolean;
  children: React.ReactNode;
}

export const BlockContainer = ({
  block,
  suggestion,
  isSelected,
  isExpanded,
  children,
}: BlockContainerProps) => {
  const suggestionStatus = suggestion?.status;
  const isRejected = suggestionStatus === "rejected";
  const isPendingExpandedSelected =
    isSelected && isExpanded && suggestionStatus === "pending";
  const useSentiment = block?.sentiment ?? suggestion?.sentiment;

  const resolveBorderColor = () => {
    if (suggestionStatus === "approved") {
      // Subtle border once approved
      return resolveSentimentSecondaryBorderColor(
        useSentiment,
        suggestion?.classification
      );
    }

    if (block.sentiment) {
      return resolveSentimentSecondaryBorderColor(block.sentiment);
    }

    // Pending (or no sentiment), only show border if expanded
    return (
      isExpanded &&
      resolveSentimentBorderColor(useSentiment, suggestion?.classification)
    );
  };

  const backgroundColor = resolveSentimentBackgroundColor(useSentiment);
  const sentimentBorder = !isRejected && resolveBorderColor();
  const baseBorder = isPendingExpandedSelected
    ? "border-transparent"
    : sentimentBorder || "border-white";
  const pendingBorderCompatClasses = isPendingExpandedSelected
    ? cn("border-2 border-dashed", sentimentBorder || "border-zinc-300")
    : "";
  const containerStyle = isPendingExpandedSelected
    ? { borderWidth: "1px", borderColor: "transparent" as const }
    : undefined;

  return (
    <div
      className={cn(
        "relative group rounded-md px-2 my-2 border",
        baseBorder,
        pendingBorderCompatClasses,
        block.sentiment && backgroundColor
      )}
      style={containerStyle}
    >
      {isPendingExpandedSelected && (
        <div
          className={cn(
            "pointer-events-none absolute inset-0 rounded-md border-2 border-dashed",
            sentimentBorder || "border-zinc-300"
          )}
        />
      )}
      {children}
    </div>
  );
};
